/**
 * ProjectDetail Page - Project summary: details + map + tracking, with
 * Users and Mix Design/Recipes panels side by side.
 *
 * Contacts and Loggers assignments still exist in the backend but are
 * intentionally hidden from this page (Project Setup revamp, Jul 2026).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import projectsService, { Project } from '../services/projects';
import ProjectUsersTab from '../components/ProjectUsersTab';
import ProjectProductsTab from '../components/ProjectProductsTab';

// Fix default marker icons for leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const projectIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
      <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="#1565C0" stroke="#FFFFFF" stroke-width="1.5"/>
      <circle cx="12.5" cy="12.5" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

/** Audit timestamps are stored as naive UTC — parse them as UTC and render
 *  in the browser's local time (see timezone convention in datetime.ts). */
const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ProjectDetail: React.FC = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Child-panel failures surface as a toast; page-level `error` (which
  // replaces the whole page via the early return) is only for loadProject.
  const [panelError, setPanelError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadProject = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await projectsService.getProject(projectId);
      setProject(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId, loadProject]);

  const handleBack = () => {
    navigate('/projects');
  };

  const handleEdit = () => {
    navigate(`/projects/${projectId}/edit`);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', minHeight: 400, alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || t('projects.projectNotFound')}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          {t('projects.backToProjects')}
        </Button>
      </Box>
    );
  }

  const lat = Number(project.latitude);
  const lon = Number(project.longitude);
  const hasPosition =
    !!project.latitude && !!project.longitude && !Number.isNaN(lat) && !Number.isNaN(lon);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mr: 2 }}>
          {t('projects.backToProjects')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {project.project_name}
        </Typography>
        {project.is_active === false && (
          <Chip
            label={t('projects.statusDeactivated', 'Deactivated')}
            color="warning"
            sx={{ mr: 2 }}
          />
        )}
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          {t('projectDetail.editProject')}
        </Button>
      </Box>

      {/* Project Info + Map + Tracking */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('projectDetail.client')}
                  </Typography>
                  <Typography>
                    {project.client || t('common.notSpecified')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('projectDetail.timezone')}
                  </Typography>
                  <Typography>
                    {project.timezone || t('common.notSpecified')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('projectDetail.address')}
                  </Typography>
                  <Typography>
                    {project.address || t('common.notSpecified')}
                  </Typography>
                  {hasPosition && (
                    <Typography variant="body2" color="text.secondary">
                      {t('projectDetail.gps')}: {project.latitude}, {project.longitude}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ height: '100%', minHeight: 260, overflow: 'hidden' }}>
            {hasPosition ? (
              <MapContainer
                center={[lat, lon]}
                zoom={13}
                style={{ height: '100%', width: '100%', minHeight: 260 }}
                scrollWheelZoom={true}
                attributionControl={false}
              >
                <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[lat, lon]} icon={projectIcon}>
                  <Popup>
                    <strong>{project.project_name}</strong>
                    {project.address && (
                      <>
                        <br />
                        {project.address}
                      </>
                    )}
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  minHeight: 260,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('projectDetail.noLocation', 'No location set. Add an address to see the map.')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Tracking sidebar — same pattern as the temperature limit groups */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper elevation={2} sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('projectDetail.dateCreated', 'Date Created')}:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatDateTime(project.date_create)}
                {project.create_by ? ` (${project.create_by})` : ''}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('projectDetail.dateLastChanged', 'Date Last Changed')}:
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatDateTime(project.date_last_modified)}
                {project.modified_by ? ` (${project.modified_by})` : ''}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              {t('projectDetail.history', 'History')}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {[
                {
                  label: t('projectDetail.historyCreated', 'Project Created'),
                  when: project.date_create,
                  who: project.create_by,
                },
                {
                  label: t('projectDetail.historyChanged', 'Last Changed'),
                  when: project.date_last_modified,
                  who: project.modified_by,
                },
              ].map((ev, i, arr) => (
                <Box key={ev.label} sx={{ display: 'flex', gap: 1.5 }}>
                  {/* dot + connector */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        mt: 0.5,
                      }}
                    />
                    {i < arr.length - 1 && (
                      <Box sx={{ width: '2px', flex: 1, bgcolor: 'grey.300', my: 0.5 }} />
                    )}
                  </Box>
                  <Box sx={{ pb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>{ev.label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDateTime(ev.when)}
                    </Typography>
                    {ev.who && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {ev.who}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Users + Mix Design / Recipes side by side */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <ProjectUsersTab
                projectId={projectId!}
                companyId={project.company_id}
                onError={(msg) => setPanelError(msg)}
                onSuccess={(msg) => setSuccess(msg)}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <ProjectProductsTab
                projectId={projectId!}
                companyId={project.company_id}
                onError={(msg) => setPanelError(msg)}
                onSuccess={(msg) => setSuccess(msg)}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar (child-panel failures) */}
      <Snackbar
        open={!!panelError}
        autoHideDuration={6000}
        onClose={() => setPanelError(null)}
      >
        <Alert severity="error" onClose={() => setPanelError(null)}>
          {panelError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectDetail;
